import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { io, Socket } from 'socket.io-client';
import { updateMetrics } from '../store/dashboardSlice';
import { updatePulseCardMetrics, setActiveSignal, setActiveBasket } from '../store/optionsSlice';
import { upsertAssessment } from '../store/fnoSlice';
import { speechService, buildSpeechText } from '../services/speech.service';
import toast from 'react-hot-toast';
import { WS_URL } from '../utils/constants';

// R117 — module-level handle to the live socket so other pages can attach
// extra listeners (e.g. the admin /e2e-tests page subscribes to e2e:*
// events without opening a second socket.io connection). Returns null
// until the hook below has connected; subscribers should fall back to
// useEffect retries until non-null.
let globalSocket: Socket | null = null;
export const getGlobalSocket = (): Socket | null => globalSocket;

export const useWebSocket = (token: string | null) => {
  const dispatch = useDispatch();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token) return;

    // Initialize socket connection
    socketRef.current = io(WS_URL, {
      auth: { token },
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5
    });
    globalSocket = socketRef.current;

    const socket = socketRef.current;

    // Connection events
    socket.on('connect', () => {
      console.log('WebSocket connected');
      toast.success('Connected to trading server');
      // Catch up on any missed data after reconnect
      dispatch(updateMetrics({}));
    });

    socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      toast.error('Disconnected from trading server');
    });

    // Trading events. Backend can emit `alert:new` for the same logical alert
    // through several paths (TradingView webhook, internal strategy alert,
    // renotify trigger, periodic re-evaluation). We dedup at the speech layer
    // by passing the alert id so the same alert is not announced multiple times.
    socket.on('alert:new', (data: any) => {
      const alertData = data.alert;
      const confirmed = alertData.parsedData?.confirmed;
      const confirmIcon = confirmed === 'Y' ? '✅' : confirmed === 'N' ? '⏳' : 'ℹ️';
      toast(`New alert: ${alertData.title}`, { icon: confirmIcon, id: `alert-${alertData.id}` });

      const speechEnabled = localStorage.getItem('speechAlertEnabled') === 'true';
      if (speechEnabled && speechService.isSupported()) {
        const action = (alertData.parsedData?.action || '').toUpperCase();
        const symbol = alertData.symbol || '';
        const speechText = buildSpeechText(action, symbol);
        const toneType = (action === 'BUY' || action === 'COVER') ? 'buy'
          : (action === 'SELL' || action === 'SHORT') ? 'sell'
          : 'alert';
        // Dedup key = alert id so a single alert announces once even if the
        // backend emits it via multiple websocket paths within DEDUP_WINDOW_MS.
        const dedupKey = alertData.id != null ? `alert:${alertData.id}` : speechText;
        if (speechText) speechService.speak(speechText, toneType, dedupKey);
      }
    });

    // System warnings (e.g. IP not whitelisted on Delta Exchange)
    socket.on('system:warning', (data: any) => {
      toast.error(data.message || 'System warning', { duration: 10000 });
    });

    socket.on('system:error', (data: any) => {
      toast.error(`System error: ${data.message || 'Unknown error'}`, { duration: 15000 });
      const speechEnabled = localStorage.getItem('speechAlertEnabled') === 'true';
      if (speechEnabled && speechService.isSupported()) {
        speechService.speak('System error. Check the app.');
      }
    });

    socket.on('order:update', (data: any) => {
      if (data.order.status === 'FILLED') {
        toast.success(`Order ${data.order.orderId} filled`);
      } else if (data.order.status === 'REJECTED') {
        toast.error(`Order ${data.order.orderId} rejected`);
      }
    });

    socket.on('dashboard:update', (data: any) => {
      dispatch(updateMetrics(data.metrics));
    });

    // Options Intelligence Module events
    socket.on('option:snapshot', (data: any) => {
      dispatch(updatePulseCardMetrics(data));
    });

    socket.on('option:signal', (data: any) => {
      dispatch(setActiveSignal(data));
      toast(`Options Signal: ${data.symbol} ${data.direction} — Score ${data.score?.toFixed(0)}`, {
        icon: data.score >= 85 ? '🎯' : data.score >= 70 ? '⚡' : '⚠️',
        duration: 8000
      });
    });

    socket.on('option:basket', (data: any) => {
      dispatch(setActiveBasket(data));
    });

    socket.on('option:risk-warning', (data: any) => {
      toast.error(data.message || 'Options risk warning', { duration: 10000 });
    });

    // FNO combined score assessment (fires a few seconds after alert arrives)
    socket.on('fno:assessment', (data: any) => {
      if (data?.alertId) dispatch(upsertAssessment(data));
    });

    // Subscribe to channels
    socket.emit('subscribe', {
      channels: ['alerts', 'orders', 'trades', 'dashboard', 'options']
    });

    return () => {
      socket.disconnect();
      if (globalSocket === socket) globalSocket = null;
    };
  }, [token, dispatch]);

  const sendMessage = (event: string, data: any) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(event, data);
    }
  };

  return { sendMessage };
};
